import React, { createContext, useContext } from 'react';
import { productDisplayNo } from '../utils/productDisplayId';

/* ⭐ PROP `t` — dua bahasa TANPA mengubah rupa lama.
   Modal ini dibuka daripada halaman Products dalam KEDUA-DUA rupa.
   Halaman v3.9 menghantar fungsi terjemahannya masuk; rupa lama tidak
   menghantar apa-apa dan kekal dengan ayat asal di bawah, huruf demi huruf.

   Kenapa bukan panggil useT() terus di sini: bahasa lalai app ialah
   English, jadi panggilan terus bermakna pelanggan yang masih pada rupa
   LAMA — iaitu semua orang sehingga v3.9 dilancarkan — tiba-tiba nampak
   modal ini bertukar bahasa. Itu regresi yang sampai kepada pelanggan.

   Bila v3.9 sudah menjadi satu-satunya rupa, prop ini boleh dibuang dan
   useT() dipanggil terus. */
const ambilTeks = (t) => (kunci, lalai, vars) => (t ? t(kunci, vars) : lalai);

/* Konteks TEMPATAN (tidak dieksport) supaya dua pembantu kecil di bawah —
   SourceBadge (bersarang dalam setiap Row) dan VariantsTable — dapat fungsi
   terjemahan tanpa setiap satu daripada ~60 panggilan <Row> terpaksa
   menghantarnya turun. Tiada provider (rupa lama) → null → ayat asal. */
const KonteksTeks = createContext(null);
const useTeks = () => ambilTeks(useContext(KonteksTeks));

const DASH = '-';

// All scrapedData keys that have their own dedicated display in the modal above.
// Anything NOT in this set is treated as an "Extra / Custom" field and auto-rendered.
const KNOWN_KEYS = new Set([
    'source', 'sourceUrl', 'resolvedUrl', 'productId', 'sources', 'fieldSources',
    'name', 'description', 'descriptionMedia', 'sellingPoints',
    'price', 'originalPrice', 'discount', 'category', 'brand',
    'shopName', 'shopUrl', 'shopRating', 'shopFollowerCount', 'shopResponseRate', 'shopBadges',
    'rating', 'ratingCount', 'soldCount',
    'variants', 'stockCount', 'stockStatus',
    'badges', 'promoLabel', 'promoEndTime',
    'shippingFee', 'shippingFrom', 'shippingEta', 'returnPolicy',
    'productVideo', 'topReviews', 'tags', 'specifications',
    'commissionRate', 'commissionAmount', 'affiliateLink',
    'rawImageUrl', 'gallery', 'rawImages',
    'launchDate', 'avgUnitPrice', 'revenue', 'liveRevenue', 'videoRevenue',
    'shoppingMallRevenue', 'creatorNumber', 'creatorConversionRatio', 'kalodataUrl',
]);

const SOURCE_STYLES = {
    sheet:    { bg: 'bg-emerald-100 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-400', label: 'SHEET' },
    kalodata: { bg: 'bg-sky-100 dark:bg-sky-950/40',         text: 'text-sky-700 dark:text-sky-400',         label: 'KALO' },
    pdp:      { bg: 'bg-indigo-100 dark:bg-indigo-950/40',   text: 'text-indigo-700 dark:text-indigo-400',   label: 'PDP' },
};

function SourceBadge({ source }) {
    const T = useTeks();
    if (!source || !SOURCE_STYLES[source]) return null;
    const s = SOURCE_STYLES[source];
    return (
        <span className={`inline-block ml-1 px-1.5 py-0.5 text-[8px] font-black rounded ${s.bg} ${s.text}`} title={T('products.scraped.badge.sourceTooltip', `Data from ${source}`, { source })}>
            {s.label}
        </span>
    );
}

function Row({ label, value, source }) {
    const display = (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0))
        ? DASH
        : value;
    return (
        <div className="flex items-start gap-3 py-2 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
            <div className="w-36 flex-shrink-0 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide pt-0.5">
                {label}
                {display !== DASH && <SourceBadge source={source} />}
            </div>
            <div className="flex-1 text-[12px] text-slate-800 dark:text-slate-200 break-words min-w-0">
                {display === DASH ? <span className="text-slate-300 dark:text-slate-600">{DASH}</span> : display}
            </div>
        </div>
    );
}

function Section({ title, icon, children }) {
    return (
        <div className="mb-4">
            <h3 className="flex items-center gap-2 text-[11px] font-black uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/30 rounded-md">
                <span className="material-symbols-outlined text-[14px]">{icon}</span>
                {title}
            </h3>
            <div className="px-3">{children}</div>
        </div>
    );
}

function Badges({ items }) {
    if (!items || items.length === 0) return <span className="text-slate-300 dark:text-slate-600">{DASH}</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {items.map((b, i) => (
                <span key={i} className="px-2 py-0.5 text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded">{String(b)}</span>
            ))}
        </div>
    );
}

function VariantsTable({ variants }) {
    const T = useTeks();
    if (!variants || variants.length === 0) return <span className="text-slate-300 dark:text-slate-600">{DASH}</span>;
    return (
        <div className="overflow-x-auto max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded">
            <table className="w-full text-[11px]">
                <thead className="bg-slate-50 dark:bg-slate-900 sticky top-0">
                    <tr>
                        <th className="text-left px-2 py-1 font-bold text-slate-600 dark:text-slate-400">{T('products.scraped.variants.name', 'Name')}</th>
                        <th className="text-left px-2 py-1 font-bold text-slate-600 dark:text-slate-400">{T('products.col.price', 'Price')}</th>
                        <th className="text-left px-2 py-1 font-bold text-slate-600 dark:text-slate-400">{T('products.scraped.variants.stock', 'Stock')}</th>
                    </tr>
                </thead>
                <tbody>
                    {variants.map((v, i) => (
                        <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                            <td className="px-2 py-1 text-slate-800 dark:text-slate-200">{v?.name || DASH}</td>
                            <td className="px-2 py-1 text-slate-800 dark:text-slate-200">{v?.price || DASH}</td>
                            <td className="px-2 py-1 text-slate-800 dark:text-slate-200">{v?.stock ?? DASH}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SpecList({ specs }) {
    if (!specs || Object.keys(specs).length === 0) return <span className="text-slate-300 dark:text-slate-600">{DASH}</span>;
    return (
        <div className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded p-2 space-y-1">
            {Object.entries(specs).map(([k, v], i) => (
                <div key={i} className="flex gap-2 text-[11px]">
                    <div className="w-24 font-bold text-slate-500 dark:text-slate-400 flex-shrink-0">{k}</div>
                    <div className="text-slate-800 dark:text-slate-200 break-words">{String(v)}</div>
                </div>
            ))}
        </div>
    );
}

function ReviewList({ reviews }) {
    if (!reviews || reviews.length === 0) return <span className="text-slate-300 dark:text-slate-600">{DASH}</span>;
    return (
        <div className="space-y-2 max-h-60 overflow-y-auto">
            {reviews.map((r, i) => (
                <div key={i} className="p-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700">
                    <div className="flex justify-between items-start gap-2 mb-1">
                        <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">{r?.author || DASH}</span>
                        <div className="flex gap-2 text-[10px] text-slate-500">
                            {r?.rating !== null && r?.rating !== undefined && <span>★ {r.rating}</span>}
                            {r?.date && <span>{r.date}</span>}
                        </div>
                    </div>
                    <p className="text-[11px] text-slate-600 dark:text-slate-400 break-words">{r?.text || DASH}</p>
                </div>
            ))}
        </div>
    );
}

function ImageGrid({ images, cols = 5 }) {
    if (!images || images.length === 0) return <span className="text-slate-300 dark:text-slate-600">{DASH}</span>;
    const gridClass = cols === 3 ? 'grid grid-cols-3 gap-1' : 'grid grid-cols-5 gap-1';
    return (
        <div className={gridClass}>
            {images.slice(0, 12).map((src, i) => (
                <a key={i} href={src} target="_blank" rel="noreferrer" className="aspect-square bg-slate-100 dark:bg-slate-800 rounded overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-indigo-400">
                    <img src={src} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                </a>
            ))}
        </div>
    );
}

function VideoList({ videos }) {
    if (!videos || videos.length === 0) return <span className="text-slate-300 dark:text-slate-600">{DASH}</span>;
    return (
        <div className="space-y-2">
            {videos.slice(0, 5).map((src, i) => (
                <div key={i} className="space-y-1">
                    <video src={src} controls className="w-full max-h-48 rounded border border-slate-200 dark:border-slate-700 bg-black" />
                    <a href={src} target="_blank" rel="noreferrer" className="text-[10px] text-indigo-500 hover:underline break-all block">{src}</a>
                </div>
            ))}
        </div>
    );
}

export default function ScrapedDataModal({ product, onClose, t }) {
    if (!product) return null;
    const s = product.scrapedData || {};
    const T = ambilTeks(t);

    const linkToSource = s.resolvedUrl || s.sourceUrl || product.link;

    return (
        <KonteksTeks.Provider value={t}>
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 animate-fadeIn" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200 dark:border-slate-700">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 p-4 border-b border-slate-200 dark:border-slate-800">
                    <div className="flex gap-3 min-w-0">
                        {product.imageUrl ? (
                            <img src={product.imageUrl} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0 bg-slate-100" onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                            <div className="w-14 h-14 rounded-lg bg-slate-100 dark:bg-slate-800 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-[10px] font-black text-indigo-500 whitespace-nowrap tabular-nums" title={T('products.card.idTooltip', 'ID produk TikTok')}>#{productDisplayNo(product)}</span>
                                <span className="px-1.5 py-0.5 text-[9px] font-black bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400 rounded uppercase">{T('products.scraped.badge.title', 'Scraped Data')}</span>
                            </div>
                            <h2 className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate">{product.name}</h2>
                            {linkToSource && (
                                <a href={linkToSource} target="_blank" rel="noreferrer" className="text-[10px] text-indigo-500 hover:underline truncate block">
                                    {linkToSource}
                                </a>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} aria-label={T('products.scraped.closeAria', 'Close')} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 flex-shrink-0">
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto py-3">
                    {(() => {
                        const fs = s.fieldSources || {};
                        const src = (k) => fs[k];
                        return (
                            <>
                                {/* Legend */}
                                <div className="px-3 pb-2 flex items-center gap-2 text-[10px] text-slate-500 dark:text-slate-400">
                                    <span>{T('products.scraped.legend.source', 'Source:')}</span>
                                    <span className="px-1.5 py-0.5 font-black rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">SHEET</span>
                                    <span className="px-1.5 py-0.5 font-black rounded bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400">KALO</span>
                                    <span className="px-1.5 py-0.5 font-black rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400">PDP</span>
                                </div>

                                <Section title={T('products.scraped.section.basic', 'Basic')} icon="info">
                                    <Row label={T('products.scraped.row.productId', 'Product ID')} value={s.productId} />
                                    <Row label={T('products.scraped.row.category', 'Category')} value={s.category} source={src('category')} />
                                    <Row label={T('products.scraped.row.brand', 'Brand')} value={s.brand} source={src('brand')} />
                                    <Row label={T('products.scraped.row.sources', 'Sources')} value={Array.isArray(s.sources) && s.sources.length > 0 ? s.sources.join(' + ') : null} />
                                </Section>

                                <Section title={T('products.scraped.section.description', 'Description')} icon="description">
                                    <Row label={T('products.scraped.row.text', 'Text')} value={s.description ? <div className="whitespace-pre-wrap">{s.description}</div> : null} source={src('description')} />
                                    <Row label={T('products.scraped.row.images', 'Images')} value={<ImageGrid images={s.descriptionMedia?.images} cols={3} />} source={s.descriptionMedia?.images?.length > 0 ? 'pdp' : undefined} />
                                    <Row label={T('products.scraped.row.videos', 'Videos')} value={<VideoList videos={s.descriptionMedia?.videos} />} source={s.descriptionMedia?.videos?.length > 0 ? 'pdp' : undefined} />
                                </Section>

                                <Section title={T('products.scraped.section.sellingPoints', 'Selling Points')} icon="bolt">
                                    <Row label={T('products.scraped.row.points', 'Points')} value={<Badges items={s.sellingPoints} />} source={src('sellingPoints')} />
                                </Section>

                                <Section title={T('products.scraped.section.analytics', 'Analytics (Kalodata)')} icon="analytics">
                                    <Row label={T('products.scraped.row.sold', 'Sold')} value={s.soldCount} source={src('soldCount')} />
                                    <Row label={T('products.scraped.row.launchDate', 'Launch Date')} value={s.launchDate} source={src('launchDate')} />
                                    <Row label={T('products.scraped.row.avgUnitPrice', 'Avg Unit Price')} value={s.avgUnitPrice} source={src('avgUnitPrice')} />
                                    <Row label={T('products.scraped.row.revenue', 'Revenue')} value={s.revenue} source={src('revenue')} />
                                    <Row label={T('products.scraped.row.liveRevenue', 'Live Revenue')} value={s.liveRevenue} source={src('liveRevenue')} />
                                    <Row label={T('products.scraped.row.videoRevenue', 'Video Revenue')} value={s.videoRevenue} source={src('videoRevenue')} />
                                    <Row label={T('products.scraped.row.mallRevenue', 'Shopping Mall Rev')} value={s.shoppingMallRevenue} source={src('shoppingMallRevenue')} />
                                    <Row label={T('products.scraped.row.creatorNumber', 'Creator Number')} value={s.creatorNumber} source={src('creatorNumber')} />
                                    <Row label={T('products.scraped.row.creatorConvRatio', 'Creator Conv. Ratio')} value={s.creatorConversionRatio} source={src('creatorConversionRatio')} />
                                    <Row label={T('products.scraped.row.commissionRate', 'Commission Rate')} value={s.commissionRate} source={src('commissionRate')} />
                                    <Row label={T('products.scraped.row.kalodataUrl', 'Kalodata URL')} value={s.kalodataUrl ? <a href={s.kalodataUrl} target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline break-all">{s.kalodataUrl}</a> : null} source={src('kalodataUrl')} />
                                </Section>

                                <Section title={T('products.scraped.section.pricePromo', 'Price & Promo')} icon="local_offer">
                                    <Row label={T('products.col.price', 'Price')} value={s.price} source={src('price')} />
                                    <Row label={T('products.scraped.row.originalPrice', 'Original Price')} value={s.originalPrice} source={src('originalPrice')} />
                                    <Row label={T('products.col.discount', 'Discount')} value={s.discount} source={src('discount')} />
                                    <Row label={T('products.scraped.row.promoLabel', 'Promo Label')} value={s.promoLabel} source={src('promoLabel')} />
                                    <Row label={T('products.scraped.row.promoEnds', 'Promo Ends')} value={s.promoEndTime} source={src('promoEndTime')} />
                                    <Row label={T('products.scraped.row.badges', 'Badges')} value={<Badges items={s.badges} />} source={src('badges')} />
                                </Section>

                                <Section title={T('products.scraped.section.shop', 'Shop')} icon="storefront">
                                    <Row label={T('products.scraped.row.shopName', 'Shop Name')} value={s.shopName} source={src('shopName')} />
                                    <Row label={T('products.scraped.row.shopUrl', 'Shop URL')} value={s.shopUrl ? <a href={s.shopUrl} target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline break-all">{s.shopUrl}</a> : null} source={src('shopUrl')} />
                                    <Row label={T('products.scraped.row.shopRating', 'Shop Rating')} value={s.shopRating} source={src('shopRating')} />
                                    <Row label={T('products.scraped.row.followers', 'Followers')} value={s.shopFollowerCount} source={src('shopFollowerCount')} />
                                    <Row label={T('products.scraped.row.responseRate', 'Response Rate')} value={s.shopResponseRate} source={src('shopResponseRate')} />
                                    <Row label={T('products.scraped.row.shopBadges', 'Shop Badges')} value={<Badges items={s.shopBadges} />} source={src('shopBadges')} />
                                </Section>

                                <Section title={T('products.scraped.section.rating', 'Rating')} icon="star">
                                    <Row label={T('products.scraped.row.rating', 'Rating')} value={s.rating} source={src('rating')} />
                                    <Row label={T('products.scraped.row.ratingCount', 'Rating Count')} value={s.ratingCount} source={src('ratingCount')} />
                                </Section>

                                <Section title={T('products.scraped.section.variantsStock', 'Variants & Stock')} icon="category">
                                    <Row label={T('products.scraped.row.variants', 'Variants')} value={<VariantsTable variants={s.variants} />} source={src('variants')} />
                                    <Row label={T('products.scraped.row.stockCount', 'Stock Count')} value={s.stockCount} source={src('stockCount')} />
                                    <Row label={T('products.scraped.row.stockStatus', 'Stock Status')} value={s.stockStatus} source={src('stockStatus')} />
                                </Section>

                                <Section title={T('products.scraped.section.shippingReturn', 'Shipping & Return')} icon="local_shipping">
                                    <Row label={T('products.scraped.row.shippingFee', 'Shipping Fee')} value={s.shippingFee} source={src('shippingFee')} />
                                    <Row label={T('products.scraped.row.shipFrom', 'Ship From')} value={s.shippingFrom} source={src('shippingFrom')} />
                                    <Row label={T('products.scraped.row.eta', 'ETA')} value={s.shippingEta} source={src('shippingEta')} />
                                    <Row label={T('products.scraped.row.returnPolicy', 'Return Policy')} value={s.returnPolicy} source={src('returnPolicy')} />
                                </Section>

                                <Section title={T('products.scraped.section.content', 'Content')} icon="play_circle">
                                    <Row label={T('products.scraped.row.productVideo', 'Product Video')} value={s.productVideo ? <a href={s.productVideo} target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline break-all">{s.productVideo}</a> : null} source={src('productVideo')} />
                                    <Row label={T('products.scraped.row.tags', 'Tags')} value={<Badges items={s.tags} />} source={src('tags')} />
                                    <Row label={T('products.scraped.row.specifications', 'Specifications')} value={<SpecList specs={s.specifications} />} />
                                    <Row label={T('products.scraped.row.topReviews', 'Top Reviews')} value={<ReviewList reviews={s.topReviews} />} source={src('topReviews')} />
                                </Section>

                                <Section title={T('products.scraped.section.affiliate', 'Affiliate')} icon="handshake">
                                    <Row label={T('products.scraped.row.commissionAmount', 'Commission Amount')} value={s.commissionAmount} source={src('commissionAmount')} />
                                    <Row label={T('products.scraped.row.affiliateLink', 'Affiliate Link')} value={s.affiliateLink ? <a href={s.affiliateLink} target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline break-all">{s.affiliateLink}</a> : null} source={src('affiliateLink')} />
                                </Section>

                                <Section title={T('products.scraped.section.gallery', 'Product Gallery')} icon="image">
                                    <Row label={T('products.scraped.row.primaryImage', 'Primary Image')} value={s.rawImageUrl ? <a href={s.rawImageUrl} target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline break-all">{s.rawImageUrl}</a> : null} />
                                    <Row label={T('products.scraped.row.gallery', 'Gallery')} value={<ImageGrid images={s.gallery || s.rawImages} />} />
                                </Section>

                                {/* Extra / Custom fields (from user-defined column mappings) */}
                                {(() => {
                                    const extraEntries = Object.entries(s).filter(([k, v]) =>
                                        !KNOWN_KEYS.has(k)
                                        && v !== null && v !== undefined && v !== ''
                                        && !(Array.isArray(v) && v.length === 0)
                                    );
                                    if (extraEntries.length === 0) return null;
                                    return (
                                        <Section title={T('products.scraped.section.extraFields', 'Extra Fields')} icon="add_circle">
                                            {extraEntries.map(([k, v]) => {
                                                let display = v;
                                                if (typeof v === 'object' && !Array.isArray(v)) {
                                                    display = <pre className="text-[10px] whitespace-pre-wrap break-words bg-slate-50 dark:bg-slate-800 p-2 rounded">{JSON.stringify(v, null, 2)}</pre>;
                                                } else if (Array.isArray(v)) {
                                                    display = <Badges items={v.map(String)} />;
                                                }
                                                return <Row key={k} label={k} value={display} source={src(k)} />;
                                            })}
                                        </Section>
                                    );
                                })()}

                                <Section title={T('products.scraped.section.meta', 'Meta')} icon="settings">
                                    <Row label={T('products.scraped.row.source', 'Source')} value={s.source} />
                                    <Row label={T('products.scraped.row.sourceUrl', 'Source URL')} value={s.sourceUrl ? <a href={s.sourceUrl} target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline break-all">{s.sourceUrl}</a> : null} />
                                    <Row label={T('products.scraped.row.resolvedUrl', 'Resolved URL')} value={s.resolvedUrl ? <a href={s.resolvedUrl} target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline break-all">{s.resolvedUrl}</a> : null} />
                                </Section>
                            </>
                        );
                    })()}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 p-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
                    <button onClick={onClose} className="px-4 py-2 text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors">
                        {T('products.scraped.close', 'Close')}
                    </button>
                </div>
            </div>
        </div>
        </KonteksTeks.Provider>
    );
}
